export const ROLE_TO_STAR = {
    "researcher": "O",
    "phd student": "B",
    "phd_student": "B",
    "placeholder":"A",
    "employee": "F",
    "teacher": "G",
    "placeholder2": "K",
    "student": "M",
    "external": "L",
};

export const STAR_COLOR_RANGES = {
    O: ["#9bb0ff", "#a4baff"],
    B: ["#a7bcff", "#bbccff"],
    A: ["#d1dbff", "#e2e7ff"],
    F: ["#f7f5ff", "#fff8f8"],
    G: ["#fff3ea", "#fff0e3"],
    K: ["#ffead5", "#ffddb4"],
    M: ["#ffcf95", "#ffbd6f"],
    L: ["#ff7b00", "#ff5200"],
};

const ROLE_TO_COLOR = {
    "instructor": "#F52B8C",
    "teacher": "#F52B8C",
    "researcher": "#BD00FF",
    "phd student": "#FF7A00",
    "phd_student": "#FF7A00",
    "student": "#FFE500",
    "other student": "#FFE500",
    "employee": "#2BF5EA",
    "alumni": "#B4F52B",
    "external": "#2B3FF5"}


export function interpolateColor(color1, color2, factor) {
    const [r1, g1, b1] = [parseInt(color1.slice(1, 3), 16), parseInt(color1.slice(3, 5), 16), parseInt(color1.slice(5, 7), 16)];
    const [r2, g2, b2] = [parseInt(color2.slice(1, 3), 16), parseInt(color2.slice(3, 5), 16), parseInt(color2.slice(5, 7), 16)];

    const r = Math.round(r1 + factor * (r2 - r1));
    const g = Math.round(g1 + factor * (g2 - g1));
    const b = Math.round(b1 + factor * (b2 - b1));

    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export function stringToHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
}

export function seededRandomGenerator(hash) {
    const constant = 2147483648; // 2^31
    const multiplier = 48271;
    let seed = hash % constant;

    return function() {
        seed = (seed * multiplier) % constant;
        return seed / constant;
    };
}

export function seededBoxMullerRandom(generatorFunc) {
    let u = generatorFunc();
    let v = generatorFunc();
    let attempts = 2;

    while(u <= Number.EPSILON && attempts > 0) {
        u = generatorFunc();
        attempts--;
    }

    attempts = 10;
    while(v <= Number.EPSILON && attempts > 0) {
        v = generatorFunc();
        attempts--;
    }

    // If we exceed the attempts, default to a safe value
    u = (u <= Number.EPSILON) ? 0.5 : u;
    v = (v <= Number.EPSILON) ? 0.5 : v;

    const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
    return Math.max(-1, Math.min(1, z));
}


function getStarColorWithRandomness(baseColor, lowerBound, upperBound, deterministicRandom) {
    if (deterministicRandom > 0) {
        return interpolateColor(baseColor, upperBound, deterministicRandom);
    } else {
        return interpolateColor(baseColor, lowerBound, -deterministicRandom);
    }
}


export function getNodeColor3d(node) {
    const role = node.statuses[0];
    const starType = ROLE_TO_STAR[role];
    const colorRange = STAR_COLOR_RANGES[starType];
    const baseColor = interpolateColor(colorRange[0], colorRange[1], 0.5);  // Average color

    const hash = stringToHash(node.id);
    const deterministicRandomGenerator = seededRandomGenerator(hash);
    const deterministicNormal = seededBoxMullerRandom(deterministicRandomGenerator) / 5;

    return getStarColorWithRandomness(baseColor, colorRange[0], colorRange[1], deterministicNormal);
}

export function getNodeColor2d(node) {
    const role = node.statuses[0];
    const colorType = ROLE_TO_COLOR[role];

    return colorType;
}

export function getContrastYIQ(hexcolor){
    // calculate the luminance of the background color and
    // decide on the text color based on that.
    //     If the luminance is below a certain threshold,
    //     you use white text; if it's above, you use black text.
    var r = parseInt(hexcolor.substr(1,2),16);
    var g = parseInt(hexcolor.substr(3,2),16);
    var b = parseInt(hexcolor.substr(5,2),16);
    var yiq = ((r*299)+(g*587)+(b*114))/1000;
    return (yiq >= 128) ? '#101010' : '#F6F6F6';
    // Example usage:
    // let backgroundColor = "#ff7e5f"; // Replace with your background color
    // let textColor = getContrastYIQ(backgroundColor);
}


export function generateNeighborInfoBoxContent(node, getNodeColor2d) {
    // Initialize an object to count the statuses
    let statusCounts = {};

    // Iterate over each neighbor to populate the statusCounts object
    node.neighbors.forEach(neighbor => {
        // Assuming each neighbor node has a 'statuses' property that's an array
        const status = neighbor.statuses[0]; // Taking the first status as primary

        // If the status is already in the object, increment its count; otherwise, add it with a count of 1
        statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    // Convert statusCounts object to an array and sort it by count, high to low
    const sortedStatuses = Object.entries(statusCounts).sort((a, b) => b[1] - a[1]);

    // Generate the HTML content for each status
    // Start building the HTML content as a list
    let infoBoxDetailsContent = '<ul class="info-legend">';

    sortedStatuses.forEach(([status, count]) => {
        // Create a temporary node object for getColorFromStatus function
        const tempNode = { statuses: [status], id:0};
        const color = getNodeColor2d(tempNode);
        // Append each status as a list item with a colored dot
        infoBoxDetailsContent += `
            <li>
                <span class="dot" style="background-color:${color};"></span>
                ${count} ${status.replace("phd", "PhD")}
            </li>`;
    });

    infoBoxDetailsContent += '</ul>';

    return infoBoxDetailsContent;
}

// Function to update the legend colors
export function updateLegend(useGradient) {
    document.querySelectorAll('.legend .dot').forEach(dot => {
        const role = dot.className.split(' ')[1]; // Assuming 'dot' and role class are always present
        // Use ROLE_TO_STAR for gradient case to get the star classification
        const starClass = ROLE_TO_STAR[role.replace('_', ' ')]; // Adjust for underscore/space discrepancy
        if (useGradient && starClass && STAR_COLOR_RANGES[starClass]) {
            const [startColor, endColor] = STAR_COLOR_RANGES[starClass];
            dot.style.background = `linear-gradient(to right, ${startColor}, ${endColor})`;
        } else {
            dot.style.background = ROLE_TO_COLOR[role] || '#000'; // Fallback color if role not found
        }
    });
}



